'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, Play, Star, CheckCircle2, Zap, Globe, Code2 } from 'lucide-react';

import Image from 'next/image';

const floatingBadges = [
  { id: 'badge-lighthouse', icon: Zap, label: 'Lighthouse 98', color: 'text-amber', delay: '0s', top: '20%', left: '5%' },
  { id: 'badge-clients', icon: Star, label: '200+ Clients', color: 'text-accent', delay: '1.5s', top: '60%', left: '2%' },
  { id: 'badge-global', icon: Globe, label: 'Global Reach', color: 'text-emerald', delay: '0.8s', top: '25%', right: '4%' },
  { id: 'badge-code', icon: Code2, label: 'Clean Code', color: 'text-secondary', delay: '2s', top: '65%', right: '3%' },
];

const slideshowImages = [
  {
    src: '/assets/images/Webmint_Presentation_1-1786532559427.jpeg',
    alt: 'Webmint presentation slide 1',
  },
  {
    src: '/assets/images/Webmint_Presentation_2-1786532559428.jpeg',
    alt: 'Webmint presentation slide 2',
  },
];

export default function HeroSection() {
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);
  const [activeSlide, setActiveSlide] = useState(0);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      setMouseX((e.clientX - rect.left) / rect.width);
      setMouseY((e.clientY - rect.top) / rect.height);
    };
    const el = heroRef.current;
    el?.addEventListener('mousemove', handleMouseMove);
    return () => el?.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slideshowImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden aurora-bg-intense grid-dot-pattern"
      id="hero"
    >
      {/* Slideshow background images */}
      {slideshowImages.map((img, index) => (
        <div
          key={img.src}
          className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
          style={{ opacity: activeSlide === index ? 1 : 0, zIndex: 0 }}
        >
          <Image
            src={img.src}
            alt={img.alt}
            fill
            className="object-cover"
            priority={index === 0}
          />
          {/* Dark overlay so text remains readable */}
          <div className="absolute inset-0 bg-black/60" />
        </div>
      ))}

      {/* Animated gradient orbs */}
      <div
        className="absolute w-[600px] h-[600px] rounded-full opacity-20 blur-3xl pointer-events-none transition-transform duration-700 ease-out"
        style={{
          background: 'radial-gradient(circle, #2563EB 0%, transparent 70%)',
          transform: `translate(${mouseX * 40}px, ${mouseY * 30}px)`,
          top: '10%',
          left: '15%',
          zIndex: 1,
        }}
      />
      <div
        className="absolute w-[500px] h-[500px] rounded-full opacity-15 blur-3xl pointer-events-none transition-transform duration-1000 ease-out"
        style={{
          background: 'radial-gradient(circle, #7C3AED 0%, transparent 70%)',
          transform: `translate(${-mouseX * 30}px, ${-mouseY * 20}px)`,
          bottom: '10%',
          right: '10%',
          zIndex: 1,
        }}
      />
      <div className="absolute w-[400px] h-[400px] rounded-full opacity-10 blur-3xl pointer-events-none animate-aurora"
        style={{ background: 'radial-gradient(circle, #06B6D4 0%, transparent 70%)', top: '40%', left: '50%', transform: 'translateX(-50%)', zIndex: 1 }}
      />

      {/* Floating badges */}
      {floatingBadges.map((badge) => {
        const BadgeIcon = badge.icon;
        return (
          <div
            key={badge.id}
            className="absolute hidden xl:flex items-center gap-2 glass-card px-3 py-2 rounded-xl animate-float"
            style={{
              top: badge.top,
              left: badge.left,
              right: badge.right,
              animationDelay: badge.delay,
              zIndex: 2,
            }}
          >
            <BadgeIcon size={14} className={badge.color} />
            <span className="text-xs font-semibold font-display text-foreground">{badge.label}</span>
          </div>
        );
      })}

      {/* Slide indicator dots */}
      <div className="absolute bottom-24 left-1/2 -translate-x-1/2 flex gap-2" style={{ zIndex: 2 }}>
        {slideshowImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setActiveSlide(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              activeSlide === index ? 'bg-white w-6' : 'bg-white/40'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Main content */}
      <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16 text-center pt-24 pb-16">
        {/* Top label */}
        <div className="inline-flex items-center gap-2 glass-card px-4 py-2 rounded-full mb-8 border border-accent/20">
          <span className="w-2 h-2 rounded-full bg-emerald animate-pulse" />
          <span className="section-label text-[11px]">Premium Digital Agency — Est. 2018</span>
        </div>

        {/* Main headline */}
        <h1 className="font-display font-extrabold text-hero-2xl text-foreground mb-6 max-w-5xl mx-auto">
          Crafting{' '}
          <span className="text-gradient-primary">Premium</span>
          <br />
          Digital{' '}
          <span className="text-gradient-cyan">Experiences</span>
        </h1>

        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed font-body">
          We build stunning websites, powerful e-commerce platforms, and bespoke digital products
          that drive real business growth — from concept to launch and beyond.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
          <Link href="#contact" className="btn-primary text-base px-8 py-4 glow-primary">
            Start Your Project
            <ArrowRight size={18} />
          </Link>
          <button className="btn-outline text-base px-8 py-4 group">
            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-primary/20 transition-all duration-200">
              <Play size={12} className="ml-0.5 text-foreground" />
            </div>
            Watch Showreel
          </button>
        </div>

        {/* Trust signals */}
        <div className="flex flex-wrap items-center justify-center gap-6 mb-16">
          {[
            { id: 'trust-delivery', text: 'On-time delivery guarantee' },
            { id: 'trust-support', text: '24/7 dedicated support' },
            { id: 'trust-revisions', text: 'Unlimited revisions' },
            { id: 'trust-performance', text: 'Performance optimized' },
          ].map((item) => (
            <div key={item.id} className="flex items-center gap-2">
              <CheckCircle2 size={14} className="text-emerald flex-shrink-0" />
              <span className="text-sm text-muted-foreground">{item.text}</span>
            </div>
          ))}
        </div>

        {/* Client logos strip */}
        <div className="border-t border-border/30 pt-10">
          <p className="text-xs font-mono-code text-muted-foreground/50 tracking-widest uppercase mb-6">
            Trusted by innovative companies worldwide
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 opacity-40">
            {[
              { id: 'logo-nexus', name: 'Nexus Corp' },
              { id: 'logo-vertex', name: 'Vertex Labs' },
              { id: 'logo-aurora', name: 'Aurora Tech' },
              { id: 'logo-prism', name: 'Prism Studio' },
              { id: 'logo-orbit', name: 'Orbit Digital' },
              { id: 'logo-zenith', name: 'Zenith Group' },
            ].map((logo) => (
              <div
                key={logo.id}
                className="text-sm font-display font-bold text-muted-foreground tracking-wider uppercase"
              >
                {logo.name}
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
          <span className="text-[10px] font-mono-code tracking-widest text-muted-foreground uppercase">Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-muted-foreground to-transparent" />
        </div>
      </div>
    </section>
  );
}